import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Eraser, Film, GripVertical, Star, Trash2 } from "lucide-react";
import api, { thumbSrc } from "@/lib/api";
import { SectionTitle, fmtBytes, formatApiError, ghostBtn } from "./shared";

export const MediaGrid = ({ media, onChange }) => {
  const [order, setOrder] = useState(media);
  const [dragId, setDragId] = useState(null);
  const [overId, setOverId] = useState(null);
  const dirty = useRef(false);

  useEffect(() => {
    setOrder(media);
  }, [media]);

  const move = (fromId, toId) => {
    if (fromId === toId) return;
    setOrder((list) => {
      const from = list.findIndex((x) => x.id === fromId);
      const to = list.findIndex((x) => x.id === toId);
      if (from < 0 || to < 0) return list;
      const next = [...list];
      const [item] = next.splice(from, 1);
      next.splice(to, 0, item);
      dirty.current = true;
      return next;
    });
  };

  const persist = async () => {
    setDragId(null);
    setOverId(null);
    if (!dirty.current) return;
    dirty.current = false;
    try {
      await api.put("/media/reorder", { ids: order.map((x) => x.id) });
      toast.success("Gallery order saved.");
      onChange();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
      setOrder(media);
    }
  };

  const toggleFeatured = async (item) => {
    try {
      await api.patch(`/media/${item.id}`, { featured: !item.featured });
      toast.success(item.featured ? "Removed from featured." : `"${item.title}" now leads the gallery.`);
      onChange();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  const remove = async (item) => {
    if (!window.confirm(`Remove "${item.title}" from the site?`)) return;
    try {
      await api.delete(`/media/${item.id}`);
      toast.success("Removed.");
      onChange();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  const clearSamples = async () => {
    if (!window.confirm("Remove all sample showcase content? Your own uploads stay.")) return;
    try {
      const { data } = await api.delete("/media-samples");
      toast.success(`${data.removed} sample items removed.`);
      onChange();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  return (
    <section className="border border-line bg-coal p-6 sm:p-8">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <SectionTitle count={order.length}>Live Media</SectionTitle>
          <p className="font-mono text-[10px] tracking-[0.15em] uppercase text-mute mt-2">
            Drag frames to reorder · ★ featured frames always lead the gallery
          </p>
        </div>
        {order.some((m) => m.seeded) && (
          <button data-testid="admin-clear-samples-button" onClick={clearSamples} className={ghostBtn}>
            <Eraser size={12} /> Remove Sample Content
          </button>
        )}
      </div>
      {order.length === 0 ? (
        <p className="font-mono text-[11px] text-mute mt-6">Nothing live yet. Upload your first frame above.</p>
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3 mt-6" data-testid="admin-media-grid">
          {order.map((item, i) => {
            const thumb = thumbSrc(item);
            const isDragging = dragId === item.id;
            const isOver = overId === item.id && dragId !== item.id;
            return (
              <div
                key={item.id}
                data-testid={`admin-media-item-${i}`}
                data-media-id={item.id}
                draggable
                onDragStart={(e) => {
                  setDragId(item.id);
                  e.dataTransfer.effectAllowed = "move";
                  e.dataTransfer.setData("text/plain", item.id);
                }}
                onDragOver={(e) => {
                  e.preventDefault();
                  if (dragId && dragId !== item.id) {
                    setOverId(item.id);
                    move(dragId, item.id);
                  }
                }}
                onDrop={(e) => e.preventDefault()}
                onDragEnd={persist}
                className={`group relative border bg-ink overflow-hidden transition-all duration-200 cursor-grab active:cursor-grabbing ${
                  isDragging ? "opacity-40 scale-95 border-amber" : isOver ? "border-amber/70" : item.featured ? "border-amber/40" : "border-line"
                }`}
              >
                <div className="aspect-square relative">
                  {thumb ? (
                    <img src={thumb} alt={item.title} loading="lazy" draggable={false} className="absolute inset-0 w-full h-full object-cover pointer-events-none" />
                  ) : (
                    <div className="absolute inset-0 flex items-center justify-center bg-raise">
                      <Film size={22} className="text-mute" />
                    </div>
                  )}
                  <span className="absolute top-2 left-2 font-mono text-[8px] tracking-[0.2em] uppercase bg-ink/80 text-cream px-2 py-1 flex items-center gap-1.5">
                    <GripVertical size={9} className="text-mute" />
                    {String(i + 1).padStart(2, "0")} · {item.kind}{item.seeded ? " · sample" : ""}
                  </span>
                  <div className="absolute top-2 right-2 flex gap-1.5">
                    <button
                      data-testid={`admin-media-feature-button-${i}`}
                      aria-label={item.featured ? `Unfeature ${item.title}` : `Feature ${item.title}`}
                      aria-pressed={item.featured}
                      onClick={() => toggleFeatured(item)}
                      className={`w-8 h-8 flex items-center justify-center bg-ink/80 border transition-all ${
                        item.featured
                          ? "text-amber border-amber/60 opacity-100"
                          : "text-ash border-line opacity-0 group-hover:opacity-100 hover:text-amber"
                      }`}
                    >
                      <Star size={13} className={item.featured ? "fill-amber" : ""} />
                    </button>
                    <button
                      data-testid={`admin-media-delete-button-${i}`}
                      aria-label={`Delete ${item.title}`}
                      onClick={() => remove(item)}
                      className="w-8 h-8 flex items-center justify-center bg-ink/80 text-ash hover:text-red-400 border border-line opacity-0 group-hover:opacity-100 transition-all"
                    >
                      <Trash2 size={13} />
                    </button>
                  </div>
                </div>
                <div className="p-3">
                  <p className="font-mono text-[10px] text-ivory truncate">{item.title}</p>
                  <p className="font-mono text-[9px] text-mute uppercase tracking-[0.15em] mt-1">
                    {item.category}{item.size ? ` · ${fmtBytes(item.size)}` : ""}
                  </p>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </section>
  );
};
